import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import type { AutomationStatsDto, FeedbackDto, FeedbackListResponse, Locale, ResourceDto, ResourcesResponse, SectionDto } from "../shared/dto";
import { isBotTransferClientFault } from "../shared/bot-transfer-reasons";
import { AutomationTimingEstimate } from "./AutomationTimingEstimate";
import {
  IMAGE_UPLOAD_MIME_TYPES,
  localUploadIssue,
  uploadIssueFromNetwork,
  uploadIssueFromResponse,
  uploadIssueLocaleKey,
  type UploadIssue
} from "./attachment-upload";
import { actionIssueFrom, actionIssueFromResponse, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "./action-error";
import { feedbackSubmitIssueKey } from "./feedback-issue";
import { VoiceableInput } from "./VoiceableInput";
import { translateCount } from "./i18n";
import { knowhowCloseGate } from "./knowhow-gate";
import { TelegramOwnershipForm } from "./TelegramOwnershipForm";
import { dropTextDraft, readTextDraft, writeTextDraft } from "./voice-draft";
import { WorkingAvatar } from "./WorkingAvatar";
import { isBotTransferFixReason } from "../shared/bot-transfer";
import { intlLanguage } from "./date-format";
import { railSubstepMarked, type RailStepProgress } from "../shared/rail-substeps";

export type M4Surface = "knowhow" | "training" | "beta" | "beta:feedback";

export function M4SectionView(props: {
  surface: M4Surface;
  sections: SectionDto[];
  csrfToken: string;
  locale: Locale;
  /** F115: keys the unsent podnět draft, so two clients never share one. */
  clientId?: string;
  membershipRole: "owner" | "collaborator";
  profilePhotoUrl?: string | null;
  t: (key: string) => string;
  onChanged: () => Promise<void>;
  onNavigate?: (surface: M4Surface) => void;
  testResources?: ResourceDto[];
  testAutomationStats?: AutomationStatsDto;
  refreshKey?: number;
  /** F123/F124: the server's beta substep marks. Never re-derived here. */
  betaProgress?: RailStepProgress | null;
}) {
  const [resources, setResources] = useState<ResourceDto[]>(props.testResources ?? []);
  const [knowhowStats, setKnowhowStats] = useState<AutomationStatsDto | undefined>(props.testAutomationStats);
  const [loading, setLoading] = useState(!props.testResources);
  const [loadError, setLoadError] = useState(false);

  const loadResources = useCallback(async (background = false) => {
    if (props.testResources) {
      setResources(props.testResources);
      setKnowhowStats(props.testAutomationStats);
      setLoading(false);
      return;
    }
    if (!background) {
      setLoading(true);
      setLoadError(false);
    }
    try {
      const response = await fetch("/api/resources", { credentials: "include" });
      if (!response.ok) throw new Error("resource_load_failed");
      const body = await response.json() as ResourcesResponse;
      setResources(body.resources);
      setKnowhowStats(body.automationStats?.knowhow_provisioning);
    } catch {
      if (!background) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [props.testAutomationStats, props.testResources]);

  // After the first paint every refresh is a quiet in-place update (no pulse,
  // no scroll jump) - actions and polls must not blank the screen.
  const reloadResources = useCallback(() => loadResources(true), [loadResources]);

  useEffect(() => {
    void loadResources();
  }, [loadResources]);

  useEffect(() => {
    const refresh = () => void reloadResources();
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reloadResources]);

  const refreshReady = useRef(false);
  useEffect(() => {
    if (!refreshReady.current) {
      refreshReady.current = true;
      return;
    }
    void reloadResources();
  }, [props.refreshKey, reloadResources]);

  const baseSurface = props.surface === "beta:feedback" ? "beta" : props.surface;
  const section = props.sections.find((item) => item.key === baseSurface) ?? null;
  const viewResources = props.testResources ?? resources;
  if (loading && !props.testResources) return <section className="m4-state" aria-live="polite"><div className="loading-pulse" /><p>{props.t("common.loading")}</p></section>;
  if (loadError && !props.testResources) return <section className="m4-empty"><p className="error-message">{props.t("resources.loadError")}</p><button className="secondary-button compact-button" type="button" onClick={() => void loadResources()}>{props.t("common.retry")}</button></section>;
  if (!section) return null;

  if (baseSurface === "knowhow") {
    return <KnowhowSection {...props} section={section} resources={viewResources} automationStats={knowhowStats} reloadResources={reloadResources} />;
  }
  if (baseSurface === "training") return <TrainingSection section={section} resources={viewResources} profilePhotoUrl={props.profilePhotoUrl} t={props.t} />;
  return <BetaSection {...props} section={section} resources={viewResources} reloadResources={reloadResources} feedbackOnly={props.surface === "beta:feedback"} />;
}

export function KnowhowSection(props: {
  section: SectionDto;
  resources: ResourceDto[];
  automationStats?: AutomationStatsDto;
  csrfToken: string;
  locale: Locale;
  profilePhotoUrl?: string | null;
  t: (key: string) => string;
  onChanged: () => Promise<void>;
  reloadResources: () => Promise<void>;
  onNavigate?: (surface: M4Surface) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [checked, setChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const closeCommandId = useRef<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!confirming) return;
    dialogRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) setConfirming(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [confirming, submitting]);
  const drive = resource(props.resources, "drive_folder");
  const urls = resource(props.resources, "url_doc");
  if (props.section.state === "done") {
    return null;
  }
  // One decision for the whole close: sources ready -> tick the box -> confirm.
  const gate = knowhowCloseGate({
    hasDrive: Boolean(drive),
    hasUrls: Boolean(urls),
    state: props.section.state,
    confirmed: checked,
    submitting
  });

  async function complete() {
    setSubmitting(true);
    setError(false);
    try {
      closeCommandId.current ??= crypto.randomUUID();
      const response = await fetch("/api/sections/knowhow/complete", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": closeCommandId.current
        },
        body: "{}"
      });
      if (!response.ok) throw new Error("knowhow_complete_failed");
      closeCommandId.current = null;
      await Promise.all([props.onChanged(), props.reloadResources()]);
      // The sources are frozen and the team starts building: move the client
      // straight to the training screen (Pavel: it should carry me forward).
      props.onNavigate?.("training");
    } catch {
      // If the response disappeared after S07 committed, the refreshed
      // section renders the closed state instead of a false failure.
      await props.onChanged().catch(() => undefined);
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }

  // Pavel's annotation: the page reads as three visible steps. 1) read the
  // rules, 2) accounts first (so the client opens the folder with the right
  // login), then upload, 3) confirm and hand over to the team.
  return (
    <section className="m4-view" aria-labelledby="knowhow-title">
      <header className="m4-heading"><p className="eyebrow blue-eyebrow">{props.t("knowhow.eyebrow")}</p><h1 id="knowhow-title">{props.t("rail.steps.knowhow")}</h1><p className="lead">{props.t("knowhow.copy")}</p></header>
      <ol className="flow-steps">
        <li className="flow-step">
          <header className="flow-step-head">
            <span className="flow-step-num step-number-token" aria-hidden="true">1</span>
            <h2>{props.t("knowhow.step1Title")}</h2>
          </header>
          <article className="knowhow-guide">
            <div className="knowhow-guide-cols">
              <div className="knowhow-col knowhow-belongs">
                <h3>{props.t("knowhow.belongsTitle")}</h3>
                <ul>
                  {[1, 2, 3, 4, 5].map((index) => (
                    <li key={index}><span className="knowhow-mark" aria-hidden="true">✓</span>{props.t(`knowhow.belongs${index}`)}</li>
                  ))}
                </ul>
              </div>
              <div className="knowhow-col knowhow-avoid">
                <h3>{props.t("knowhow.avoidTitle")}</h3>
                <ul>
                  {[1, 2, 3, 4, 5].map((index) => (
                    <li key={index}><span className="knowhow-mark" aria-hidden="true">✕</span>{props.t(`knowhow.avoid${index}`)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        </li>
        <li className="flow-step">
          <header className="flow-step-head">
            <span className="flow-step-num step-number-token" aria-hidden="true">2</span>
            <h2>{props.t("knowhow.step2Title")}</h2>
          </header>
          <GoogleAccountBlock resources={props.resources} csrfToken={props.csrfToken} profilePhotoUrl={props.profilePhotoUrl} t={props.t} onSaved={props.reloadResources} />
          <div className="resource-grid">
            <ResourceCard resource={drive} kind="folder" title={props.t("knowhow.driveTitle")} missing={props.t("knowhow.preparingDrive")} automationStats={props.automationStats} t={props.t} />
            <ResourceCard resource={urls} kind="links" title={props.t("knowhow.urlsTitle")} missing={props.t("knowhow.preparingUrls")} t={props.t} />
          </div>
          <KnowhowUploadRules t={props.t} />
          <PartnerMaterialsBlock resources={props.resources} t={props.t} />
        </li>
        <li className="flow-step">
          <header className="flow-step-head">
            <span className="flow-step-num step-number-token" aria-hidden="true">3</span>
            <h2>{props.t("knowhow.step3Title")}</h2>
          </header>
          <div className="section-action">
            <p className="section-action-note">{props.t("knowhow.confirmCopy")}</p>
            <div className="knowhow-precheck">
              <p>{props.t("knowhow.precheckTitle")}</p>
              <ul>
                {[1, 2, 3].map((index) => <li key={index}>{props.t(`knowhow.precheck${index}`)}</li>)}
              </ul>
            </div>
            <label className="knowhow-check">
              <input
                type="checkbox"
                checked={checked}
                disabled={!gate.canConfirm}
                onChange={(event) => {
                  setChecked(event.target.checked);
                  if (!event.target.checked) setConfirming(false);
                }}
              />
              <span>{props.t("knowhow.confirmCheckbox")}</span>
            </label>
            {!confirming && (
              <button className="primary-button" type="button" disabled={!gate.canSubmit} onClick={() => setConfirming(true)}>{props.t("knowhow.complete")}</button>
            )}
            {confirming && (
              <div className="knowhow-dialog-backdrop">
                <div ref={dialogRef} tabIndex={-1} className="knowhow-confirm" role="alertdialog" aria-modal="true" aria-labelledby="knowhow-confirm-title" aria-describedby="knowhow-confirm-copy">
                  <h3 id="knowhow-confirm-title">{props.t("knowhow.dialogTitle")}</h3>
                  <p id="knowhow-confirm-copy">{props.t("knowhow.dialogCopy")}</p>
                  <div className="knowhow-confirm-actions">
                    <button className="primary-button" type="button" disabled={submitting} onClick={() => void complete()}>{submitting ? props.t("knowhow.submitting") : props.t("knowhow.dialogAgree")}</button>
                    <button className="secondary-button" type="button" disabled={submitting} onClick={() => setConfirming(false)}>{props.t("knowhow.dialogDisagree")}</button>
                  </div>
                </div>
              </div>
            )}
            {gate.blockReasonKey ? <p>{props.t(gate.blockReasonKey)}</p> : null}
            {error && <p className="message error-message" role="alert">{props.t("knowhow.error")}</p>}
          </div>
        </li>
      </ol>
    </section>
  );
}

// F176 (Tomas Pohl 2026-10-20): the build reads only files lying directly in
// the folder, links only from the link doc, and needs one clean solo voice
// recording. Clients who sorted files into subfolders or sent a PDF of video
// titles lost them silently, so the rules sit right under the folders and
// step 3 repeats them as a checklist.
function KnowhowUploadRules({ t }: { t: (key: string) => string }) {
  return (
    <article className="knowhow-rules" aria-labelledby="knowhow-rules-title">
      <h3 id="knowhow-rules-title">{t("knowhow.rulesTitle")}</h3>
      <ol>
        {(["Folder", "Links", "Voice"] as const).map((rule) => (
          <li key={rule}>
            <strong>{t(`knowhow.rules${rule}Title`)}</strong>
            <span>{t(`knowhow.rules${rule}Body`)}</span>
          </li>
        ))}
      </ol>
    </article>
  );
}

// Materials the partner supplied ON BEHALF of the client (meeting 24.7. D3):
// truthful attribution ("nahráno parťákem"), files download through the
// client attachment route, links open directly. Nothing to do here for the
// client - the block only shows what the team already added.
function PartnerMaterialsBlock({ resources, t }: { resources: ResourceDto[]; t: (key: string) => string }) {
  const items = partnerMaterialItems(resources);
  if (!items.length) return null;
  return (
    <article className="resource-card partner-materials">
      <div className="resource-card-head">
        <h3>{t("knowhow.partnerTitle")}</h3>
        <span className="partner-materials-chip">{t("knowhow.partnerBy")}</span>
      </div>
      <p className="resource-card-hint">{t("knowhow.partnerHint")}</p>
      <ul className="partner-materials-list">
        {items.map((item) => {
          const id = String(item.id ?? "");
          const href = item.kind === "file"
            ? `/api/attachments/${String(item.attachment_id ?? "")}`
            : String(item.url ?? "#");
          return (
            <li key={id}>
              <a href={href} target="_blank" rel="noreferrer">{String(item.label ?? "")}</a>
              {typeof item.note === "string" && item.note && <small>{String(item.note)}</small>}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

function partnerMaterialItems(resources: ResourceDto[]): Array<Record<string, unknown>> {
  const catalog = resources.find((row) => row.key === "partner_materials");
  return catalog && Array.isArray((catalog.value as { items?: unknown[] }).items)
    ? (catalog.value as { items: Array<Record<string, unknown>> }).items
    : [];
}

/**
 * F115: how long "Zpřístupňujeme" may stand before it admits it is late. The
 * card promises 3 to 5 minutes; the same 15 minutes the Předání step waits for
 * its own facts (F110 `HANDOVER_FACT_DELAY_MS`) is generous against that
 * promise and still short enough to catch the run that never happened.
 */
export const KNOWHOW_SHARE_DELAY_MS = 15 * 60_000;

/**
 * The clock is the entry's own `requested_at`, written when the client named
 * the account (`mergeGoogleAccountValue`). An entry with no timestamp is a
 * pre-F115 row: it keeps the plain waiting state rather than inventing an age,
 * the same rule the handover card follows for a client with no opening moment.
 */
export function shareIsDelayed(requestedAt: string | null | undefined, now = Date.now()): boolean {
  if (typeof requestedAt !== "string" || !requestedAt) return false;
  const startedAt = Date.parse(requestedAt);
  return Number.isFinite(startedAt) && now - startedAt >= KNOWHOW_SHARE_DELAY_MS;
}

// The primary account is answered in q1; here the client sees every account
// the folder is shared with and can ADD more (Pavel: a colleague may upload
// the materials, teams share the folder with more people). The add form can
// always be closed again without saving.
export function GoogleAccountBlock(props: { resources: ResourceDto[]; csrfToken: string; profilePhotoUrl?: string | null; t: (key: string) => string; onSaved: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const account = resource(props.resources, "knowhow_google_account");
  // Refreshing the surface data must not throw the reader back to the top of
  // the page (Pavel: the screen jumped after adding an account).
  const refreshKeepingScroll = useCallback(async () => {
    const y = window.scrollY;
    await props.onSaved();
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [props.onSaved]);
  // "Zpřístupňujeme" flips to "Nasdíleno" on its own: while any account waits
  // for the runner, poll quietly instead of asking the client to reload.
  const anyPending = Boolean(account) && (
    String(account!.value.shared_email ?? "").toLowerCase() !== String(account!.value.email ?? "").toLowerCase()
    || (Array.isArray(account!.value.extra) && account!.value.extra.filter(isRecord)
      .some((entry) => String(entry.shared_email ?? "").toLowerCase() !== String(entry.email ?? "").toLowerCase()))
  );
  useEffect(() => {
    if (!anyPending) return;
    const timer = window.setInterval(() => void refreshKeepingScroll(), 15_000);
    return () => window.clearInterval(timer);
  }, [anyPending, refreshKeepingScroll]);
  if (!account) {
    return (
      <article className="google-account-card">
        <label htmlFor="knowhow-google">{props.t("knowhow.googleLabel")}</label>
        <GoogleAccountForm csrfToken={props.csrfToken} t={props.t} onSaved={props.onSaved} />
      </article>
    );
  }
  const primaryEmail = String(account.value.email ?? "");
  const primaryShared = String(account.value.shared_email ?? "").toLowerCase() === primaryEmail.toLowerCase();
  const extras = Array.isArray(account.value.extra)
    ? account.value.extra.filter(isRecord).map((entry) => ({
        email: String(entry.email ?? ""),
        shared: String(entry.shared_email ?? "").toLowerCase() === String(entry.email ?? "").toLowerCase(),
        requestedAt: typeof entry.requested_at === "string" ? entry.requested_at : null
      }))
    : [];
  const accounts = [
    {
      email: primaryEmail,
      shared: primaryShared,
      requestedAt: typeof account.value.requested_at === "string" ? account.value.requested_at : null
    },
    ...extras
  ].map((item) => ({ ...item, delayed: !item.shared && shareIsDelayed(item.requestedAt) }));
  const anyDelayed = accounts.some((item) => item.delayed);
  return (
    <article className="google-account-card knowhow-accounts">
      <p className="knowhow-accounts-intro">{props.t("knowhow.accountsIntro")}</p>
      <ul className="knowhow-account-list">
        {accounts.map((item) => (
          <li key={item.email}>
            <strong className="break-text">{item.email}</strong>
            <span
              className={`knowhow-account-state status-pill ${item.shared ? "pill-done" : "pill-team"}`}
              data-account-state={item.shared ? "shared" : item.delayed ? "delayed" : "pending"}
            >
              {props.t(item.shared
                ? "knowhow.accountShared"
                : item.delayed ? "knowhow.accountDelayed" : "knowhow.accountPending")}
            </span>
          </li>
        ))}
      </ul>
      {anyPending && (
        <div className="knowhow-active-status" role="status" aria-live="polite">
          <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />
          <div>
            <strong>{props.t("knowhow.activeTitle")}</strong>
            {/* F115 (audit finding 4): "Zpřístupňujeme" had no end. The account
                said the same word after three minutes and after three days, so
                a sharing that never lands reads exactly like one that is about
                to. Past the promised window the card says so, in the SAME
                sentence the Předání step already uses for a fact that is taking
                too long (F110 `delayedCopy`). */}
            <p>{props.t(anyDelayed ? "knowhow.activeDelayed" : "knowhow.activeEta")}</p>
            <small>{props.t("knowhow.activeNotify")}</small>
          </div>
        </div>
      )}
      {adding ? (
        <GoogleAccountForm
          csrfToken={props.csrfToken}
          add
          t={props.t}
          onSaved={async () => { setAdding(false); await refreshKeepingScroll(); }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <p className="knowhow-account-add">
          <button className="quiet-link" type="button" onClick={() => setAdding(true)}>{props.t("knowhow.addAccount")}</button>
          <small>{props.t("knowhow.addAccountHint")}</small>
        </p>
      )}
    </article>
  );
}

function GoogleAccountForm(props: { csrfToken: string; add?: boolean; t: (key: string) => string; onSaved?: () => Promise<void>; onCancel?: () => void }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  // N03: the server refuses the seventh account by NAME (400
  // `too_many_accounts`, MAX_GOOGLE_ACCOUNTS = 6). "Zkus to znovu" could
  // never clear it - the client would press the same button forever.
  const [issue, setIssue] = useState<ActionIssue>("generic");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("sending");
    try {
      const response = await fetch("/api/sections/knowhow/google-account", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        body: JSON.stringify({ email: email.trim(), ...(props.add ? { add: true } : {}) })
      });
      if (!response.ok) {
        setIssue(await actionIssueFrom(response));
        setState("error");
        return;
      }
      setState("sent");
      await props.onSaved?.();
    } catch {
      setIssue(actionIssueFromThrown());
      setState("error");
    }
  }

  if (state === "sent") return <p className="message success-message">{props.t("knowhow.googleDone")}</p>;
  return (
    <>
      <form className="home-invite-form" onSubmit={submit}>
        <input
          id="knowhow-google"
          type="email"
          inputMode="email"
          placeholder={props.t("knowhow.googlePlaceholder")}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoFocus={props.add}
          disabled={state === "sending"}
        />
        <button className="secondary-button compact-button" type="submit" disabled={state === "sending" || !email.trim()}>
          {props.t(props.add ? "knowhow.addAccountButton" : "knowhow.googleButton")}
        </button>
        {props.onCancel && (
          <button className="ghost-button compact-button" type="button" disabled={state === "sending"} onClick={props.onCancel}>
            {props.t("common.cancel")}
          </button>
        )}
      </form>
      {state === "error" && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(issue))}</p>}
    </>
  );
}

// Exported for tests/ui-training-wait.test.ts (D4: no countdown, calm copy).
export function TrainingSection({ section, resources, profilePhotoUrl, t }: { section: SectionDto; resources: ResourceDto[]; profilePhotoUrl?: string | null; t: (key: string) => string }) {
  const done = section.state === "done";
  if (done) return null;
  // No countdown (D4, meeting 2026-07-24): a ticking clock builds pressure
  // and lies across weekends. The calm truth: 2-3 pracovní dny + an email
  // when the Buddy is ready. The living orb stays.
  return (
    <section className="m4-view training-view" aria-labelledby="training-title">
      <header className="m4-heading"><p className="eyebrow blue-eyebrow">{t("training.eyebrow")}</p><h1 id="training-title">{t("rail.steps.training")}</h1><p className="lead">{t("training.copy")}</p></header>
      <div className="training-stage working">
        {/* Meeting 24.7.: "bude pulsovat jeho avatar" - the client's own face
            breathes in the orb while their Buddy trains; generic orb only
            when no photo exists yet. */}
        <WorkingAvatar size="stage" profilePhotoUrl={profilePhotoUrl} />
        <p className="training-calm">{t("training.calm")}</p>
        <p className="training-eta">{t("training.eta")}</p>
      </div>
    </section>
  );
}

// Exported for tests/ui-beta-transfer.test.ts (D15: 4 steps, server-backed locks).
export function BetaSection(props: {
  section: SectionDto;
  resources: ResourceDto[];
  csrfToken: string;
  locale: Locale;
  clientId?: string;
  membershipRole: "owner" | "collaborator";
  t: (key: string) => string;
  onChanged: () => Promise<void>;
  reloadResources: () => Promise<void>;
  feedbackOnly?: boolean;
  /**
   * F124: which of the three beta substeps the SERVER counts as closed. The
   * page reads this map instead of deciding on its own, so the rail's arc and
   * the ticks on this page can never disagree.
   */
  betaProgress?: RailStepProgress | null;
}) {
  const trial = resource(props.resources, "trial_link");
  const telegram = resource(props.resources, "handover_telegram");
  const bot = resource(props.resources, "bot_transfer_state")?.value;
  const [text, setText] = useState("");
  // F115 (audit finding 7): the podnět field is the one place in the product
  // where the client writes a long text that nothing saved. Every questionnaire
  // answer autosaves and the page promises exactly that, so losing this one to
  // a reload (or to an incoming call on a phone) reads as the app throwing the
  // work away. Same mechanism the material comment already uses (F98,
  // src/sections/Notes.tsx): localStorage, keyed by CLIENT, offered back and
  // dropped only once the podnět is really saved.
  const [draftRestored, setDraftRestored] = useState(false);
  const draftKey = props.clientId;
  const [audioId, setAudioId] = useState<string | undefined>();
  const [screenshot, setScreenshot] = useState<File | null>(null);
  // Tester attribution ("schránka na podněty od testerů"): the client marks
  // whose eyes saw it - their own, or a tester's (optionally named).
  const [source, setSource] = useState<"self" | "tester">("self");
  const [testerName, setTesterName] = useState("");
  const [history, setHistory] = useState<FeedbackDto[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // A failed screenshot names its own reason (F62), and since F114 so does the
  // podnět POST: the server's own code decides the sentence.
  const [error, setError] = useState<ActionIssue | null>(null);
  const [uploadIssue, setUploadIssue] = useState<UploadIssue | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<ActionIssue | null>(null);
  // Own feedback opens with the trial. Tester invitations and tester-attributed
  // feedback wait for the verified bot transfer.
  const botUsername = trial && typeof trial.value.bot_username === "string" && trial.value.bot_username
    ? String(trial.value.bot_username)
    : null;
  const botState = typeof bot?.state === "string" ? String(bot.state) : null;
  const transferred = botState === "transferred";
  // F102: a client-fault failure of the automatic transfer rides the resource
  // as client_fix. Only reasons the client can act on ever render; an unknown
  // value falls back to the working state rather than a blank error. F112 made
  // that list shared (shared/bot-transfer.ts) - it used to be typed out here,
  // so a reason added on the server side would have rendered as "pracujeme"
  // forever - and added the partner's own "vratil jsem ti to k oprave", whose
  // note IS the sentence the client reads.
  const clientFixRaw = bot?.client_fix as { reason?: unknown; note?: unknown } | undefined;
  const clientFixReason = !transferred
    && clientFixRaw && isBotTransferFixReason(clientFixRaw.reason)
    ? clientFixRaw.reason
    : null;
  const clientFixNote = typeof clientFixRaw?.note === "string" ? clientFixRaw.note : "";
  // 🔴 F134b: the instance has changed hands, so there is nothing left to ask
  // us to change. The server refuses the podnět by name
  // (409 `ownership_transferred`); a form that stays on screen to collect that
  // refusal is a dead control, so the panel says the sentence instead. Read
  // from the SAME fact the server reads (`owner_role_set_at`), never from the
  // beta section - an admin who closes beta early has not handed anything over.
  const ownershipTransferred = typeof telegram?.value.owner_role_set_at === "string"
    && telegram.value.owner_role_set_at.trim().length > 0;
  const enabled = !ownershipTransferred
    && (props.feedbackOnly || Boolean(trial) && ["next", "working"].includes(props.section.state));
  // F124: the two substeps the page could not observe before. The mark is a
  // fact the SERVER holds; this component only reports the click and reads
  // back what came home. A marked substep stays fully USABLE - the client
  // invites more testers and re-opens their Buddy after it is ticked.
  const chatDone = railSubstepMarked(props.betaProgress, "chat");
  const testersDone = railSubstepMarked(props.betaProgress, "testers");
  const markSubstep = useCallback(async (mark: "beta_chat" | "beta_invite") => {
    if (!props.clientId) return;
    try {
      const response = await fetch(`/api/client-steps/${mark}/confirm`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          // A stable command id per client: every later click REPLAYS the
          // first one, so the moment the client met their Buddy stays the
          // moment it happened and no click writes a second time.
          "Idempotency-Key": `${mark}:${props.clientId}`
        },
        body: "{}"
      });
      // A refused mark is silent on purpose: the client came here to open a
      // link, and the link works either way. Nothing is claimed that the
      // server did not accept - the tick only follows a reloaded fact.
      if (response.ok) await props.onChanged();
    } catch {
      // Offline: the link still opens. The mark is retried on the next click.
    }
  }, [props.clientId, props.csrfToken, props.onChanged]);

  // While the team prepares or verifies the transfer, poll quietly so the
  // step flips on its own - the client never has to reload.
  const waitingForTransfer = Boolean(telegram) && !transferred;
  useEffect(() => {
    if (!waitingForTransfer) return;
    const timer = window.setInterval(() => void props.reloadResources(), 15_000);
    return () => window.clearInterval(timer);
  }, [waitingForTransfer, props.reloadResources]);

  async function acceptBot() {
    setAccepting(true);
    setAcceptError(null);
    try {
      const response = await fetch("/api/delivery/bot-accepted", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: "{}"
      });
      if (!response.ok) {
        setAcceptError(await actionIssueFrom(response));
        return;
      }
      const body = await response.json().catch(() => null) as { ok?: boolean; error?: string } | null;
      if (body?.ok !== true) {
        setAcceptError(actionIssueFromResponse(200, body?.error));
        return;
      }
      await Promise.all([props.reloadResources(), props.onChanged()]);
    } catch {
      setAcceptError(actionIssueFromThrown());
    } finally {
      setAccepting(false);
    }
  }

  // The leftover is OFFERED back, never re-sent behind the client's back: it
  // lands in the field with the same "we kept what you wrote" note the walk
  // recorder uses.
  useEffect(() => {
    if (!draftKey) return;
    const saved = readTextDraft("beta", draftKey);
    if (!saved.trim()) return;
    setText(saved);
    setDraftRestored(true);
  }, [draftKey]);

  const editText = useCallback((value: string) => {
    setText(value);
    setDraftRestored(false);
    if (draftKey) writeTextDraft("beta", draftKey, value);
  }, [draftKey]);

  const loadHistory = useCallback(async () => {
    const response = await fetch("/api/feedback", { credentials: "include" });
    if (response.ok) setHistory(((await response.json()) as FeedbackListResponse).feedback);
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  async function submit() {
    if (!enabled || (!text.trim() && !audioId && !screenshot)) return;
    // A file the browser already knows will be refused never costs the client
    // an upload, and never hides behind the podnět's own error.
    const localIssue = screenshot ? localUploadIssue(screenshot, IMAGE_UPLOAD_MIME_TYPES) : null;
    if (localIssue) {
      setError(null);
      setUploadIssue(localIssue);
      return;
    }
    setSubmitting(true);
    setError(null);
    setUploadIssue(null);
    try {
      const screenshotId = screenshot ? await uploadScreenshot(screenshot, props.csrfToken) : null;
      const response = await fetch("/api/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          text: text.trim() || null,
          screenshotAttachmentId: screenshotId,
          audioAttachmentId: audioId ?? null,
          source,
          testerName: source === "tester" ? testerName.trim() || null : null
        })
      });
      // F114: read the body before anything throws. A caller that throws first
      // has already lost the code the server took the trouble to name.
      if (!response.ok) {
        setError(await actionIssueFrom(response));
        return;
      }
      // Dropped only once the podnět really landed: a refused send leaves the
      // text exactly where the client can try again.
      if (draftKey) dropTextDraft("beta", draftKey);
      setDraftRestored(false);
      setText("");
      setAudioId(undefined);
      setScreenshot(null);
      setSource("self");
      setTesterName("");
      await Promise.all([loadHistory(), props.onChanged()]);
    } catch (submitError) {
      if (submitError instanceof UploadFailure) setUploadIssue(submitError.issue);
      else setError(actionIssueFromThrown());
    } finally {
      setSubmitting(false);
    }
  }

  // The checks the browser can make before the client presses Odeslat: a phone
  // photo the pipeline cannot read, or a file over the cap. Naming them here
  // means the client never waits for an upload that was going to be refused.
  function selectScreenshot(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setScreenshot(file);
    setUploadIssue(file ? localUploadIssue(file, IMAGE_UPLOAD_MIME_TYPES) : null);
  }

  const invites = typeof trial?.value.invite_limit === "number" ? trial.value.invite_limit : 10;
  const trialUrl = trial ? String(trial.value.url) : null;
  // Two links (Pavel): the owner's own chat link and a separate invite link
  // for testers. Until the Instance Creator reports invite_url, testers use
  // the same link. The server withholds invite_url until the bot transfer is
  // verified, so before that the tester step shows only its lock.
  const inviteUrl = trial && typeof trial.value.invite_url === "string" ? String(trial.value.invite_url) : trialUrl;
  const telegramId = telegram && typeof telegram.value.telegram_id === "string" && telegram.value.telegram_id
    ? String(telegram.value.telegram_id)
    : null;

  return (
    <section className="m4-view" data-step-surface-key={props.feedbackOnly ? "feedback" : "beta"} aria-labelledby="beta-title">
      <header className="m4-heading">
        <p className="eyebrow blue-eyebrow">{props.t("beta.eyebrow")}</p>
        <h1 id="beta-title">{props.feedbackOnly ? props.t("rail.steps.feedback") : trial ? props.t("rail.steps.beta") : props.t("beta.title")}</h1>
        {/* 🔴 F134b: the lead is an INVITATION to write ("napiš nám, co se
            stalo"), printed 100 px above the sentence that says podněty are
            over. A page must not say two opposite things at once (the F128
            lesson, caught the same way: by looking at the shot). */}
        <p className="lead">{props.t(
          ownershipTransferred ? "beta.feedbackClosedLead"
            : props.feedbackOnly ? "beta.feedbackCopy"
            : transferred ? "beta.copyTransferred"
            : "beta.copy"
        )}</p>
      </header>
      <ol className="flow-steps">
        {/* K06: the transfer used to DELETE steps 1 and 2 from the page, so a
            client whose bot had just become theirs was left with a lone item
            numbered "3" and no link to their own Buddy. A finished step is
            marked finished, never removed: the numbering stays 1-2-3 and the
            chat link survives the moment it becomes most useful. */}
        {!props.feedbackOnly && (
          <>
        <li className={`flow-step${chatDone ? " flow-step-done" : ""}`} data-beta-step="1" data-beta-step-done={chatDone ? "true" : "false"}>
          <header className="flow-step-head">
            <span className={`flow-step-num step-number-token${chatDone ? " flow-step-num-done is-done" : ""}`} aria-hidden="true">{chatDone ? "✓" : "1"}</span>
            <h2>{props.t("beta.step1Title")}</h2>
          </header>
          <p className="flow-step-copy">{props.t("beta.step1Copy")}</p>
          {/* F124: opening the chat IS the step. The link keeps working after
              the tick - the client comes back to their own Buddy every day. */}
          {trialUrl
            ? <a
                className="secondary-button trial-link"
                href={trialUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => { if (!chatDone) void markSubstep("beta_chat"); }}
              >{props.t(transferred ? "beta.openChatOwned" : "beta.openChat")}</a>
            : <span className="link-placeholder">{props.t("beta.linkPlaceholder")}</span>}
        </li>
        {/* Step 2 (D15): the bot changes hands BEFORE testers arrive. The
            step completes only when the team verified the transfer - the
            checkmark mirrors bot_transfer_state, never a self-declared done. */}
        <li className={`flow-step${transferred ? " flow-step-done" : ""}`} data-beta-step="2" data-beta-step-done={transferred ? "true" : "false"}>
          <header className="flow-step-head">
            <span className={`flow-step-num step-number-token${transferred ? " flow-step-num-done is-done" : ""}`} aria-hidden="true">{transferred ? "✓" : "2"}</span>
            <h2>{props.t("beta.step2Title")}</h2>
          </header>
          <p className="flow-step-copy">{props.t("beta.step2Copy")}</p>
          {!telegram ? (
            // A verified transfer without the stored account is a data gap, not
            // a task: asking for the username again would invite the client to
            // redo a step the team already closed.
            transferred
              ? <p className="message success-message" role="status">{props.t("beta.transfer.doneNote")}</p>
              : props.membershipRole === "owner"
                ? <TelegramOwnershipForm csrfToken={props.csrfToken} botUsername={botUsername} t={props.t} onSaved={() => props.reloadResources()} />
                : <p className="handover-telegram-note">{props.t("transfer.ownerOnly")}</p>
          ) : (
            <article className="bot-transfer-card">
              <div className="handover-card-heading">
                <div>
                  {/* The kicker used to promise the transfer in the future
                      tense above a PŘEVEDENO pill. Invisible while the step
                      vanished after the transfer; permanent once it stays. */}
                  <p className="card-kicker">{props.t(transferred ? "beta.transfer.accountKickerDone" : "beta.transfer.accountKicker")}</p>
                  <strong className="break-text">@{String(telegram.value.username)}{telegramId ? ` · ID ${telegramId}` : ""}</strong>
                </div>
                <span className={`status-pill ${transferred ? "pill-done" : clientFixReason || botState === "awaiting_client" ? "pill-client" : "pill-team"}`}>
                  {props.t(`beta.transfer.state.${transferred ? "transferred" : clientFixReason ? "fix_needed" : botState && ["preparing", "awaiting_client", "client_confirmed"].includes(botState) ? botState : "preparing"}`)}
                </span>
              </div>
              {/* F102, F44: a failure the CLIENT can fix names its reason and
                  offers the form again. Internal failures never reach here -
                  the server leaves the resource alone and the client keeps
                  the honest "pracujeme". */}
              {clientFixReason && !transferred ? (
                <>
                  <p className="message error-message" role="alert" data-bot-transfer-fix={clientFixReason}>
                    {props.t(`beta.transfer.fix.${clientFixReason}`)
                      .replace("{username}", String(telegram.value.username))
                      .replace("{note}", clientFixNote)}
                  </p>
                  {props.membershipRole === "owner"
                    ? <TelegramOwnershipForm csrfToken={props.csrfToken} botUsername={botUsername} t={props.t} onSaved={() => props.reloadResources()} />
                    : <p className="handover-telegram-note">{props.t("transfer.ownerOnly")}</p>}
                </>
              ) : (
                <>
                  {/* F102: the submit started the transfer by itself, so the
                      wait state says the team is WORKING (F53 idiom), not that
                      an accept button will appear - in the automatic path the
                      2FA confirmation IS the transfer and no one accepts
                      anything. */}
                  {(!botState || botState === "preparing") && !transferred && (
                    <div className="bot-transfer-working">
                      <WorkingAvatar size="card" />
                      <p className="flow-step-copy">{props.t("beta.transfer.autoWorking")}</p>
                    </div>
                  )}
                  {botState === "awaiting_client" && props.membershipRole === "owner" && (
                    <button className="primary-button bot-accept" type="button" disabled={accepting} onClick={() => void acceptBot()}>
                      {accepting ? props.t("beta.transfer.accepting") : props.t("beta.transfer.accept")}
                    </button>
                  )}
                  {botState === "client_confirmed" && <p className="flow-step-copy">{props.t("beta.transfer.confirmedNote")}</p>}
                  {transferred && <p className="message success-message" role="status">{props.t("beta.transfer.doneNote")}</p>}
                  {acceptError && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(acceptError))}</p>}
                </>
              )}
            </article>
          )}
        </li>
          </>
        )}
        {/* F124: hotovo != zamek. The tester step is marked done on the first
            copied link and stays fully open - "testeru muzu pozvat vic". */}
        {!props.feedbackOnly && <li
          className={`flow-step${testersDone ? " flow-step-done" : transferred ? "" : " flow-step-locked"}`}
          data-beta-step="3"
          data-beta-step-done={testersDone ? "true" : "false"}
        >
          <header className="flow-step-head">
            <span className={`flow-step-num step-number-token${testersDone ? " flow-step-num-done is-done" : transferred ? "" : " is-locked"}`} aria-hidden="true">{testersDone ? "✓" : "3"}</span>
            <h2>{props.t("beta.step3Title")}</h2>
          </header>
          {transferred ? (
            <>
              <p className="flow-step-copy">{props.t("beta.step3Copy")}</p>
              <p className="beta-invites-note">{translateCount(props.locale, "beta.invites", invites)}</p>
              {inviteUrl
                ? <InviteLinkRow url={inviteUrl} t={props.t} onCopied={() => { if (!testersDone) void markSubstep("beta_invite"); }} />
                : <span className="link-placeholder">{props.t("beta.inviteLinkPlaceholder")}</span>}
              {/* The special tip on an important spot (Pavel): testing doubles
                  as the reference harvest for the first launch. */}
              <article className="beta-tip">
                <h3>{props.t("beta.tipTitle")}</h3>
                <p>{props.t("beta.tipCopy")}</p>
              </article>
            </>
          ) : (
            <p className="flow-step-lock">{props.t("beta.lockTesters")}</p>
          )}
        </li>}
        {props.feedbackOnly && <li className="feedback-only-panel">
          {ownershipTransferred
            ? <p className="feedback-closed-note" data-feedback-closed="ownership_transferred" role="status">
                {props.t("beta.feedbackOwnershipClosed")}
              </p>
            : !transferred && <p className="feedback-transfer-note">{props.t("beta.feedbackBeforeTransfer")}</p>}
          {!ownershipTransferred && <article className="feedback-form">
            {/* A7/A8: this card used to reprint `beta.feedbackCopy`, the page
                lead 161 px above it, and in a LARGER size - the page said the
                same sentence twice and the hierarchy read backwards. The lead
                stays (every step page in this product carries one) and the card
                keeps its heading, which is invitation enough above the form.
                h2, not h3: with no h2 on this surface an h3 skipped a level,
                the exact law the questionnaire wrote down last round. */}
            <h2>{props.t("beta.feedbackTitle")}</h2>
            <div className="feedback-source segmented" role="radiogroup" aria-label={props.t("beta.sourceLabel")}>
              <button
                className={`feedback-source-option${source === "self" ? " active" : ""}`}
                type="button" role="radio" aria-checked={source === "self"}
                onClick={() => setSource("self")}
              >{props.t("beta.sourceSelf")}</button>
              <button
                className={`feedback-source-option${source === "tester" ? " active" : ""}`}
                type="button" role="radio" aria-checked={source === "tester"}
                data-feedback-source="tester"
                disabled={!transferred}
                aria-disabled={!transferred}
                onClick={() => { if (transferred) setSource("tester"); }}
              >{props.t("beta.sourceTester")}</button>
              {source === "tester" && (
                <input
                  className="feedback-tester-name"
                  type="text"
                  value={testerName}
                  maxLength={120}
                  placeholder={props.t("beta.testerNamePlaceholder")}
                  aria-label={props.t("beta.testerNamePlaceholder")}
                  onChange={(event) => setTesterName(event.target.value)}
                />
              )}
            </div>
            <label className={`screenshot-drop${screenshot ? " has-file" : ""}`} htmlFor="feedback-screenshot">
              <span className="screenshot-drop-icon" aria-hidden="true" />
              <span className="screenshot-drop-copy">
                <strong>{props.t("beta.screenshot")}</strong>
                <small>{props.t("beta.screenshotHint")}</small>
                {screenshot && <em>{props.t("beta.selected")} {screenshot.name}</em>}
              </span>
              <input id="feedback-screenshot" type="file" accept="image/png,image/jpeg,image/webp" disabled={!enabled || submitting} onChange={selectScreenshot} />
            </label>
            {draftRestored && (
              <p className="message info-message walk-draft-restored" role="status" data-feedback-draft-restored="true">
                {props.t("voice.failure.textDraftRestored")}
              </p>
            )}
            <div className="feedback-voice-field">
              <VoiceableInput scope="feedback_audio" value={text} attachmentId={audioId} csrfToken={props.csrfToken} mini t={props.t} onTextChange={editText} onAttachmentReady={(item) => setAudioId(item.attachmentId)} onAttachmentClear={() => setAudioId(undefined)} />
            </div>
            <button className="primary-button feedback-submit" type="button" disabled={!enabled || submitting || (!text.trim() && !audioId && !screenshot)} onClick={() => void submit()}>{submitting ? props.t("beta.submitting") : props.t("beta.submit")}</button>
            {uploadIssue && (
              <p className="message error-message" role="alert">
                {props.t(uploadIssueLocaleKey(uploadIssue, "beta.upload"))}
              </p>
            )}
            {error && <p className="message error-message" role="alert">{props.t(feedbackSubmitIssueKey(error))}</p>}
          </article>}
          <FeedbackHistory history={history} locale={props.locale} t={props.t} closed={ownershipTransferred} />
        </li>}
      </ol>
    </section>
  );
}

// A copyable tester link: the owner sends it out as-is.
function InviteLinkRow({ url, t, onCopied }: {
  url: string;
  t: (key: string) => string;
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
      // F124: the copy is the only observable moment of "I invited testers".
      // It fires only on a REAL copy - a blocked clipboard means the client
      // still has to select the link by hand, which we cannot see.
      onCopied?.();
    } catch {
      // Clipboard blocked: the client selects the visible link by hand.
    }
  }
  return (
    <div className="invite-link-row">
      <input className="invite-link-input" type="text" readOnly value={url} aria-label={t("beta.inviteLinkLabel")} onFocus={(event) => event.target.select()} />
      <button className="secondary-button compact-button" type="button" onClick={() => void copy()}>
        {copied ? t("beta.copied") : t("beta.copyInvite")}
      </button>
    </div>
  );
}

export function ResourceCard({
  resource: item,
  kind,
  title,
  missing,
  automationStats,
  t
}: {
  resource: ResourceDto | null;
  kind: "folder" | "links";
  title: string;
  missing: string;
  automationStats?: AutomationStatsDto;
  t: (key: string) => string;
}) {
  return (
    <article className="resource-card">
      <span className="resource-icon" aria-hidden="true">
        {kind === "folder" ? (
          <svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M4 5.5A1.5 1.5 0 0 1 5.5 4h4l2 2.4h7A1.5 1.5 0 0 1 20 7.9v10.6a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13Z" opacity="0.9" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M10.6 13.4a1 1 0 0 1 0-1.4l2.8-2.8a3 3 0 0 1 4.3 4.2l-1.5 1.5a1 1 0 1 1-1.4-1.4l1.4-1.5a1 1 0 0 0-1.4-1.4l-2.8 2.8a1 1 0 0 1-1.4 0Zm2.8-2.8a1 1 0 0 1 0 1.4l-2.8 2.8a3 3 0 0 1-4.3-4.2l1.5-1.5a1 1 0 0 1 1.4 1.4L7.8 12a1 1 0 0 0 1.4 1.4l2.8-2.8a1 1 0 0 1 1.4 0Z" /></svg>
        )}
      </span>
      <h2>{title}</h2>
      {/* Instructional hint instead of the raw resource label (Pavel): the
          card tells the client exactly what to put where. */}
      <p>{t(kind === "folder" ? "knowhow.folderHint" : "knowhow.docHint")}</p>
      {item
        ? <a className="secondary-button resource-link" href={String(item.value.url)} target="_blank" rel="noreferrer">{t(kind === "folder" ? "knowhow.openFolder" : "knowhow.openDoc")}</a>
        : (
          <div className="resource-missing">
            <p>{missing}</p>
            <AutomationTimingEstimate stats={automationStats} t={t} />
          </div>
        )}
    </article>
  );
}

// Exported for tests/ui-beta-history.test.ts. Processed podněty collapse
// under a fold (same system the partner queue uses) so a long-running beta
// does not bury the open ones - the wall of ZPRACOVANÝ was the stress case.
export function FeedbackHistory({ history, locale, t, closed = false }: {
  history: FeedbackDto[];
  locale: Locale;
  t: (key: string) => string;
  // F134b: the empty state invites the client to "start with one of the tests
  // above". After the handover there is nothing above and nothing to start.
  closed?: boolean;
}) {
  const item = (entry: FeedbackDto) => (
    <li key={entry.id}>
      <div>
        <span className={`status-pill ${entry.status === "new" ? "pill-team" : "pill-done"}`}>{t(`beta.status.${entry.status}`)}</span>
        {entry.source === "tester" && <span className="tester-chip">{entry.testerName ? t("beta.fromTesterNamed").replace("{name}", entry.testerName) : t("beta.fromTester")}</span>}
        <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt, locale)}</time>
      </div>
      {entry.text && <p>{entry.text}</p>}
      <div className="feedback-media">
        {entry.screenshot && <a className="quiet-link" href={entry.screenshot.url}>{t("beta.openScreenshot")}</a>}
        {entry.audio && <a className="quiet-link" href={entry.audio.url}>{t("beta.playAudio")}</a>}
      </div>
    </li>
  );
  const open = history.filter((entry) => entry.status === "new");
  const processed = history.filter((entry) => entry.status !== "new");
  return (
    <section className="feedback-history" aria-labelledby="feedback-history-title">
      <h2 id="feedback-history-title">{t("beta.historyTitle")}</h2>
      {history.length === 0 && <p>{t(closed ? "beta.historyEmptyClosed" : "beta.historyEmpty")}</p>}
      {open.length > 0 && <ol>{open.map(item)}</ol>}
      {processed.length > 0 && (
        <details className="feedback-processed">
          <summary>{t("beta.historyProcessed").replace("{count}", String(processed.length))}</summary>
          <ol>{processed.map(item)}</ol>
        </details>
      )}
    </section>
  );
}

/**
 * The screenshot upload names its own failure (F62, same system as the q1
 * photo after F44). The old code threw one nameless "upload" error, so a
 * refused file, a full quota and a dropped connection all read as "Screenshot
 * ted nejde nahrat" - a sentence the client can do nothing with. Throws an
 * UploadFailure carrying the issue; the form turns it into the one sentence
 * that fits.
 */
class UploadFailure extends Error {
  constructor(readonly issue: UploadIssue) {
    super("upload");
  }
}

async function uploadScreenshot(file: File, csrfToken: string): Promise<string> {
  const form = new FormData();
  form.set("scope", "feedback_screenshot");
  form.set("file", file);
  let response: Response;
  try {
    response = await fetch("/api/attachments", { method: "POST", credentials: "include", headers: { "X-Launchpad-CSRF": csrfToken }, body: form });
  } catch {
    throw new UploadFailure(uploadIssueFromNetwork(navigator.onLine));
  }
  const body = await response.json().catch(() => ({})) as { attachmentId?: string; error?: string };
  if (!response.ok || !body.attachmentId) {
    throw new UploadFailure(uploadIssueFromResponse(response.status, body.error));
  }
  return body.attachmentId;
}

function resource(resources: ResourceDto[], key: ResourceDto["key"]): ResourceDto | null {
  return resources.find((item) => item.key === key) ?? null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function formatDateTime(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Prague" }).format(new Date(value));
}
